import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X, ChevronLeft, Send, Loader2, PartyPopper } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const variants = {
  enter: (d) => ({ x: d > 0 ? 80 : -80, opacity: 0, scale: 0.97 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (d) => ({ x: d > 0 ? -80 : 80, opacity: 0, scale: 0.97 }),
};

function QuestionStep({ q, idx, total, val, setVal, onNext }) {
  const pick = (answer) => {
    setVal({ ...val, answer });
    if (answer === "yes") setTimeout(() => onNext({ ...val, answer }), 220);
  };
  const noteOk = (val.note || "").trim().length >= 3;
  return (
    <div className="space-y-6" data-testid={`opening-step-${idx}`}>
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Soru {idx + 1} / {total}</div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold leading-snug" data-testid="opening-question-text">{q.text}</h2>
      <div className="grid grid-cols-2 gap-3">
        {[["yes", "Evet", Check, "emerald"], ["no", "Hayır", X, "red"]].map(([k, l, Icon, c]) => {
          const on = val.answer === k;
          return (
            <motion.button key={k} whileTap={{ scale: 0.95 }} onClick={() => pick(k)} data-testid={`opening-answer-${k}`}
                           className={`h-28 rounded-3xl border-2 flex flex-col items-center justify-center gap-2 font-display text-xl font-bold transition-colors ${
                             on ? (c === "emerald" ? "bg-emerald-600 border-emerald-500 text-white" : "bg-red-600 border-red-500 text-white")
                                : "bg-slate-900/70 border-slate-800 text-slate-200 hover:border-slate-600"}`}>
              <Icon size={30} /> {l}
            </motion.button>
          );
        })}
      </div>
      <AnimatePresence>
        {val.answer === "no" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
            <label className="text-sm font-semibold text-red-300">Açıklama zorunlu</label>
            <Textarea autoFocus rows={3} value={val.note || ""} onChange={(e) => setVal({ ...val, note: e.target.value })}
                      placeholder="Sorunu kısaca açıklayın..." className="bg-slate-950/60 border-slate-700" data-testid="opening-note" />
            <Button disabled={!noteOk} onClick={() => onNext(val)} className="w-full py-6 bg-blue-600 hover:bg-blue-500 rounded-2xl" data-testid="opening-next">Devam</Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Review({ questions, answers, onEdit }) {
  return (
    <div className="space-y-3" data-testid="opening-review">
      <h2 className="font-display text-2xl font-bold">Kontrol edin</h2>
      {questions.map((q, i) => {
        const a = answers[q.id];
        return (
          <button key={q.id} onClick={() => onEdit(i)} className="w-full text-left rounded-2xl border border-slate-800 bg-slate-900/60 p-3 flex gap-3 hover:border-slate-600" data-testid={`opening-review-${i}`}>
            <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${a?.answer === "yes" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
              {a?.answer === "yes" ? <Check size={15} /> : <X size={15} />}
            </span>
            <span className="min-w-0"><span className="block text-sm font-medium">{q.text}</span>
              {a?.answer === "no" && <span className="block text-xs text-red-300 mt-0.5">{a.note}</span>}</span>
          </button>
        );
      })}
    </div>
  );
}

export function OpeningWizard({ questions, branchName, onDone }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const total = questions.length;
  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); };
  const cur = questions[step];
  const next = (v) => { setAnswers((a) => ({ ...a, [cur.id]: v })); go(reviewing ? total : step + 1); setReviewing(false); };
  const edit = (i) => { setReviewing(true); go(i); };
  const submit = async () => {
    setSaving(true);
    try {
      await http.post("/opening/submit", { answers: questions.map((q) => ({ qid: q.id, ...answers[q.id] })) });
      setDone(true);
      setTimeout(onDone, 1600);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); if (e?.response?.status === 409) onDone(); }
    finally { setSaving(false); }
  };
  if (done) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="py-16 flex flex-col items-center gap-4 text-center" data-testid="opening-success">
        <motion.div initial={{ rotate: -20 }} animate={{ rotate: 0 }} className="h-20 w-20 rounded-3xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400"><PartyPopper size={36} /></motion.div>
        <div className="font-display text-2xl font-bold">Açılış listesi kaydedildi</div>
        <p className="text-sm text-slate-400">Kolay gelsin, iyi satışlar!</p>
      </motion.div>
    );
  }
  return (
    <div className="rounded-3xl border border-slate-800 bg-slate-950/60 overflow-hidden" data-testid="opening-wizard">
      <div className="px-4 sm:px-6 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={() => go(step - 1)} disabled={step === 0} className="p-2 -ml-2 rounded-xl hover:bg-slate-800 disabled:opacity-30" data-testid="opening-back"><ChevronLeft size={20} /></button>
          <div className="text-sm font-semibold">{branchName} · Mağaza Açılışı</div>
          <div className="w-9" />
        </div>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <motion.div className="h-full bg-blue-500" initial={false} animate={{ width: `${(Math.min(step, total) / total) * 100}%` }} />
        </div>
      </div>
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div key={step} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }} className="p-4 sm:p-6">
            {step < total
              ? <QuestionStep q={cur} idx={step} total={total} val={answers[cur.id] || {}} setVal={(v) => setAnswers((a) => ({ ...a, [cur.id]: v }))} onNext={next} />
              : <Review questions={questions} answers={answers} onEdit={edit} />}
          </motion.div>
        </AnimatePresence>
      </div>
      {step >= total && (
        <div className="p-4 sm:p-6 pt-0">
          <Button onClick={submit} disabled={saving} className="w-full py-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-base" data-testid="opening-submit">
            {saving ? <Loader2 className="animate-spin" /> : <><Send size={16} className="mr-2" /> Listeyi Gönder</>}
          </Button>
        </div>
      )}
    </div>
  );
}
